/**
 * Checks if a string is empty after trimming whitespace.
 *
 * @param str - The string to check.
 * @returns True if the string is empty or contains only whitespace; otherwise, false.
 */
export function isEmptyString(str?: string): boolean {
  if (str === undefined) {
    return true;
  }

  return str.trim().length === 0;
}

/**
 * Removes non-alphanumeric characters from a string, including '-', '+', and other special characters.
 *
 * @param str - The string to process.
 * @returns The string with non-alphanumeric characters removed.
 *
 * @remarks
 * This regex replaces any character that is not a letter or number with an empty string.
 */
export function stripNonAlphaNum(str?: string): string {
  if (str === undefined) {
    return '';
  }

  return str.replace(/[^a-zA-Z0-9]+/g, '');
}

/**
 * Converts a string to lowercase and removes non-alphanumeric characters.
 *
 * @param str - The string to process.
 * @returns The processed string suitable for case-insensitive comparisons.
 */
export function matchableStringAlphaNum(str?: string): string {
  if (str === undefined) {
    return '';
  }

  return stripNonAlphaNum(str.toLowerCase());
}

/**
 * Removes specific special characters from a string.
 *
 * @param str - The string to process.
 * @returns The string with specific special characters removed.
 *
 * @remarks
 * Adjust the regex to include any characters you want to remove.
 * Here we're removing '-', '+', and whitespace.
 */
export function stripSpecialCharacters(str?: string): string {
  if (str === undefined) {
    return '';
  }

  return str.replace(/[-+\s]/g, '');
}

/**
 * Trims the string, converts it to lowercase, and removes special characters.
 *
 * @param str - The string to process.
 * @returns The processed string suitable for case-insensitive matching.
 */
export function caseInsensitiveMatchableString(str?: string): string {
  if (str === undefined) {
    return '';
  }

  return stripSpecialCharacters(str.trim().toLowerCase());
}

/**
 * Compares two strings case-insensitively after removing special characters.
 *
 * @param str1 - The first string to compare.
 * @param str2 - The second string to compare.
 * @returns True if the processed strings are equal; otherwise, false.
 */
export function caseInsensitiveStringCompare(str1?: string, str2?: string): boolean {
  if (str1 === undefined || str2 === undefined) {
    return false;
  }

  return caseInsensitiveMatchableString(str1) === caseInsensitiveMatchableString(str2);
}
